/**
 * Shared capacity math — provider-agnostic pure functions.
 *
 * Ported from the prototype's `slice-data.js` / `capNumbers()`. The capacity screen keeps
 * the interactive state (year, active levers, units, threshold, rollout plan) in a local
 * reducer and calls into here, so the SKU view and the capacity view share one
 * implementation of the same arithmetic. Everything below is pure: no globals, no DOM.
 */

import type {
  CapacityModel,
  LeverFamily,
  LeverId,
  NpiFamily,
  ReloStop,
  RollPlan,
  Sku,
  SpaceLeverId,
} from "./types";

/** Planning horizon, when a drop serves none of its own. The breach year is derived — see
 *  `firstBreachYear` — never assumed to be any particular one of these. */
export const CAP_YEARS: readonly number[] = [2026, 2027, 2028, 2029];

/** Levers that free SKU-slots (the capital lever raises the ceiling instead). */
export const SPACE_LEVERS: readonly SpaceLeverId[] = ["relo", "conv", "pack"];

/**
 * Velocity spectrum for SKU relocation — from the conservative non-movers out to the slowest
 * movers the drop can resolve. Looser = more candidates, more space freed.
 *
 * A LIVE table, like `CHANNELS` / `FIELD_SCALE` in `snapshot.ts`: the copy below is the demo
 * spectrum, and `hydrate()` overwrites it in place with the stops the drop ships — both their
 * thresholds and their wording, because a drop can only resolve movement as finely as its own
 * window allows. The sliders, the labels and `famCount` all read it synchronously, so one
 * table keeps them in step; `LeverFamily.perStop` is indexed by position in it.
 */
export const RELO_STOPS: ReloStop[] = [
  { mv: 0, short: "Non-movers", head: "0 shipments in 2025", mult: 1.0 },
  { mv: 10, short: "≤ 10 / yr", head: "shipped 10x or fewer in 2025", mult: 1.55 },
  { mv: 20, short: "≤ 20 / yr", head: "shipped 20x or fewer in 2025", mult: 2.05 },
  { mv: 30, short: "≤ 30 / yr", head: "shipped 30x or fewer in 2025", mult: 2.48 },
  { mv: 100, short: "≤ 100 / yr", head: "shipped 100x or fewer in 2025", mult: 3.35 },
];

/* ===== 4.4 Increase capacity — capacity arrives in whole automation units ===== */
/** SKU-slots per automation unit. */
export const UNIT_SKU = 2000;
/** £m of capital per automation unit. */
export const UNIT_COST = 1.4;
/** m² of floor space per automation unit. */
export const UNIT_SPACE = 450;
/** Finite site expansion envelope, m². */
export const ENVELOPE_M2 = 1800;
export const MAX_UNITS = Math.floor(ENVELOPE_M2 / UNIT_SPACE);

export function unitLead(units: number): string {
  return `${9 + (units - 1) * 2}–${12 + (units - 1) * 2} months`;
}

/* ===== NPI rollout ===== */

/** The year before `year` in the horizon, or `undefined` at the first year. */
export function prevYear(year: number): number | undefined {
  return CAP_YEARS[CAP_YEARS.indexOf(year) - 1];
}

/** Default plan: the whole family total lands in its release year. */
export function defaultRoll(families: readonly NpiFamily[]): RollPlan {
  const roll: RollPlan = {};
  for (const f of families) {
    const inc: Record<number, number> = {};
    for (const y of CAP_YEARS) {
      inc[y] = 0;
    }
    inc[f.rel] = f.total;
    roll[f.id] = inc;
  }
  return roll;
}

/** Cumulative SKUs introduced by (and including) `year`. */
export function cumAt(roll: RollPlan, familyId: string, year: number): number {
  let c = 0;
  for (const yr of CAP_YEARS) {
    c += roll[familyId]?.[yr] ?? 0;
    if (yr === year) {
      break;
    }
  }
  return c;
}

/** Cumulative locked in before `year` — the floor a slider can't go below. */
export function cumBefore(roll: RollPlan, familyId: string, year: number): number {
  const p = prevYear(year);
  return p === undefined ? 0 : cumAt(roll, familyId, p);
}

/** Cumulative under the *scheduled* plan (everything on release). */
export function schedCumAt(family: NpiFamily, year: number): number {
  return year >= family.rel ? family.total : 0;
}

/** SKUs held at `year` under the current rollout plan. */
export function heldAt(roll: RollPlan, year: number, model: CapacityModel): number {
  let h = model.capd.base2026;
  for (const f of model.npiFamilies) {
    h += cumAt(roll, f.id, year);
  }
  return Math.round(h);
}

/** SKUs held at `year` under the scheduled plan. */
export function schedHeldAt(year: number, model: CapacityModel): number {
  let h = model.capd.base2026;
  for (const f of model.npiFamilies) {
    h += schedCumAt(f, year);
  }
  return Math.round(h);
}

/**
 * First plotted year whose scheduled NPI intake crosses `ceiling`, or `null` if none does.
 *
 * Lives here because three callers need the same answer — the screen's opening year, its note, and
 * the agent — and 2027 was hardcoded in one of them: correct for this plan by coincidence, wrong for
 * any drop whose families release later. `ceiling` defaults to the served figure but takes the one
 * in force, so an edited ceiling moves the breach year with it.
 */
export function firstBreachYear(model: CapacityModel, ceiling = model.capd.ceil): number | null {
  const years = model.years?.length ? model.years : CAP_YEARS;
  const roll = defaultRoll(model.npiFamilies);
  return years.find((y) => heldAt(roll, y, model) > ceiling) ?? null;
}

/** True when the plan has been staged away from the scheduled rollout. */
export function npiAdjusted(roll: RollPlan, model: CapacityModel): boolean {
  return model.npiFamilies.some((f) =>
    CAP_YEARS.some((y) => cumAt(roll, f.id, y) !== schedCumAt(f, y)),
  );
}

/**
 * Set family `family`'s cumulative-by-`year` to `value` and return a new plan.
 * Floor = prior-year cumulative (build-up only), cap = family total; later increments
 * are clamped so cumulative never exceeds the total.
 */
export function setRoll(roll: RollPlan, family: NpiFamily, year: number, value: number): RollPlan {
  const flo = cumBefore(roll, family.id, year);
  const v = Math.max(flo, Math.min(family.total, Math.round(value)));
  const inc: Record<number, number> = { ...roll[family.id] };
  inc[year] = v - flo;
  let cum = v;
  for (const y2 of CAP_YEARS) {
    if (y2 <= year) {
      continue;
    }
    let add = inc[y2] ?? 0;
    if (cum + add > family.total) {
      add = Math.max(0, family.total - cum);
    }
    inc[y2] = add;
    cum += add;
  }
  return { ...roll, [family.id]: inc };
}

/** Native range-thumb width, px — slider fills are inset by half of it. */
const NF_TW = 15;

/** Slider-fill stop position, inset by half the thumb so the boundary lands under the dot. */
export function npiPos(fraction: number): string {
  const fr = Math.max(0, Math.min(1, fraction));
  return `calc(${(fr * 100).toFixed(2)}% + ${(NF_TW / 2 - fr * NF_TW).toFixed(2)}px)`;
}

/**
 * Rollout slider background: prior years locked in as one solid black baseline, this
 * year's addition in red on top, remainder light gray.
 */
export function npiBarGrad(roll: RollPlan, family: NpiFamily, year: number): string {
  const total = family.total;
  const ff = Math.min(1, cumBefore(roll, family.id, year) / total);
  const vf = Math.min(1, cumAt(roll, family.id, year) / total);
  const s: string[] = [];
  if (ff > 0.001) {
    s.push(`#141414 ${npiPos(0)}`, `#141414 ${npiPos(ff)}`);
  }
  if (vf > ff + 0.001) {
    s.push(`#EB1700 ${npiPos(ff)}`, `#EB1700 ${npiPos(vf)}`);
  }
  s.push(`#EDEBE8 ${npiPos(vf)}`, `#EDEBE8 ${npiPos(1)}`);
  return `linear-gradient(90deg,${s.join(",")})`;
}

/* ===== lever stacking =====
 * Candidate SKUs and SKU-slots freed track ~1:1 — each relocated / converted /
 * rationalised SKU vacates roughly one pick slot.
 */

/**
 * Candidate SKUs in a family at the current threshold (relocation only widens).
 *
 * A drop that carries movement per SKU serves the real count for every stop, and that count is
 * what the slider, the roll-up and the ceiling chart read. The `mult` curve below is a
 * demo-fit interpolation kept for the demo provider only — applied to real data it invents
 * candidates (and `countsServed()` is what stops any caption calling it a count).
 */
export function famCount(family: LeverFamily, reloStop: number): number {
  if (family.leverId !== "relo") {
    return family.base;
  }
  const served = family.perStop?.[reloStop];
  if (served !== undefined) {
    return served;
  }
  const mult = RELO_STOPS[reloStop]?.mult ?? 1;
  return Math.round(family.base * (1 + (mult - 1) * family.gv));
}

/**
 * True when every family carries a served count for every stop — i.e. when the figures on
 * screen are counted rather than interpolated. Drives the wording, so an interpolation is
 * never captioned as a count.
 */
export function countsServed(families: readonly LeverFamily[]): boolean {
  return (
    families.length > 0 &&
    families.every((f) => f.leverId !== "relo" || f.perStop?.length === RELO_STOPS.length)
  );
}

/** Families still included in the action. */
export function leverKept(
  families: readonly LeverFamily[],
  removedIds: readonly string[],
): LeverFamily[] {
  return families.filter((f) => !removedIds.includes(f.id));
}

/** Full candidate pool at the current threshold, ignoring exclusions. */
export function leverPool(families: readonly LeverFamily[], reloStop: number): number {
  return families.reduce((a, f) => a + famCount(f, reloStop), 0);
}

/** Accepted candidate SKUs (≈ SKU-slots freed, 1:1). */
export function leverAccepted(
  families: readonly LeverFamily[],
  removedIds: readonly string[],
  reloStop: number,
): number {
  return leverKept(families, removedIds).reduce((a, f) => a + famCount(f, reloStop), 0);
}

/**
 * Accepted SKU ids across every ACTIVE space lever, unioned rather than summed — a relocation
 * family and a pack-rationalisation family are drawn from the same brand/pack members, so their
 * accepted sets are not disjoint, and adding two levers' counts double-counts a SKU eligible for
 * both. `null` when a kept family carries no served `memberIds` (a drop old enough to predate
 * them): the caller then has no way to union and must fall back to the naive per-lever sum.
 */
export function acceptedUnion(
  active: readonly LeverId[],
  families: Partial<Record<SpaceLeverId, readonly LeverFamily[]>>,
  removedFam: Partial<Record<SpaceLeverId, readonly string[]>>,
  reloStop: number,
): Set<number> | null {
  const ids = new Set<number>();
  for (const id of SPACE_LEVERS) {
    if (!active.includes(id)) {
      continue;
    }
    for (const f of leverKept(families[id] ?? [], removedFam[id] ?? [])) {
      if (!f.memberIds) {
        return null;
      }
      f.memberIds.forEach((skuId, i) => {
        const stop = f.memberStop?.[i];
        if (stop === undefined || stop <= reloStop) {
          ids.add(skuId);
        }
      });
    }
  }
  return ids;
}

export interface CapNumbersInput {
  year: number;
  roll: RollPlan;
  model: CapacityModel;
  /** Levers toggled on. */
  active: readonly LeverId[];
  /** Automation units dialled on the capital lever. */
  units: number;
  /** Index into `RELO_STOPS`. */
  reloStop: number;
  families: Partial<Record<SpaceLeverId, readonly LeverFamily[]>>;
  removedFam: Partial<Record<SpaceLeverId, readonly string[]>>;
  /**
   * Pick-face ceiling before any automation units, when the screen is running a scenario against a
   * ceiling other than the served one.
   *
   * The served figure is a planning INPUT — no export of sales and stock states how many SKUs a
   * building's pick faces hold — so the screen lets it be changed, and every percentage here is
   * read against whatever is in force. Omit to use the served ceiling.
   */
  ceilBase?: number;
}

export interface CapNumbers {
  /** Held before levers. */
  held: number;
  /** SKU-slots freed by the active space levers. */
  freedSku: number;
  /** Capital lever active. */
  incr: boolean;
  /** Ceiling including added units. */
  ceiling: number;
  /** Held after levers. */
  effHeld: number;
  /** All percentages are of the *base* ceiling, so the grid axis stays fixed. */
  basePct: number;
  effPct: number;
  ceilPct: number;
}

export function capNumbers(input: CapNumbersInput): CapNumbers {
  const { year, roll, model, active, units, reloStop, families, removedFam } = input;
  const held = heldAt(roll, year, model);
  const union = acceptedUnion(active, families, removedFam, reloStop);
  // ponytail: the sum fallback only fires on a snapshot old enough to carry no memberIds at
  // all — every family the build ships today serves them, so this path is a safety net, not
  // the normal one, and it can double-count a SKU eligible for two space levers.
  let freedSku = union?.size ?? 0;
  if (!union) {
    for (const id of SPACE_LEVERS) {
      if (!active.includes(id)) {
        continue;
      }
      freedSku += leverAccepted(families[id] ?? [], removedFam[id] ?? [], reloStop);
    }
  }
  const incr = active.includes("cap");
  const addCap = incr ? units * UNIT_SKU : 0;
  const base = input.ceilBase && input.ceilBase > 0 ? input.ceilBase : model.capd.ceil;
  const ceiling = base + addCap;
  const effHeld = Math.max(0, held - freedSku);
  return {
    held,
    freedSku,
    incr,
    ceiling,
    effHeld,
    basePct: (held / base) * 100,
    effPct: (effHeld / base) * 100,
    ceilPct: (ceiling / base) * 100,
  };
}

/* ===== estimated pick-face depth =====
 * `Sku.faces` is boxes held converted to faces on a fixed depth scale the build picks for the
 * plot, so a SUM of it measures relative footprint — it is NOT a count of the site's pick faces.
 * `site.pickCap` / `site.occ` are planning-deck figures on a different basis, and on a real drop
 * the two are an order of magnitude apart, so quoting Σfaces as "pick faces" beside the ceiling
 * asserted that a selection needs more faces than the whole site has.
 *
 * Every surface therefore quotes a SHARE of the field's own estimated depth — true whatever the
 * scale is, because the scale cancels — and names the basis with `DEPTH_BASIS`. The build states
 * the scale itself in its `pick-location` provenance note, which the report pages now render.
 */

/** What "depth" means, in one clause, wherever a depth share is printed. */
export const DEPTH_BASIS =
  "boxes held converted to faces on a fixed scale — a relative footprint, not a count of the site's planning faces";

/**
 * A set's share of the field's estimated depth, as a percentage string.
 *
 * Two decimals below 0.1% so a thin tail reads as a small share rather than a flat 0.0%, and an
 * empty field reads as `n/a` — a share of nothing is not 0%.
 */
export function depthPct(faces: number, baseFaces: number): string {
  if (!(baseFaces > 0)) {
    return "n/a";
  }
  const p = (faces / baseFaces) * 100;
  return (p > 0 && p < 0.1 ? p.toFixed(2) : p.toFixed(1)) + "%";
}

/* ===== position coincidence =====
 * Why the field needs cluster glyphs at all: a mark is one (velocity, value) position, and a
 * portfolio of lens variants puts thousands of SKUs on the same one. Counted, never assumed —
 * these figures are what the copy quotes, so they are derived from the served columns here.
 */

export interface Coincidence {
  total: number;
  /** Distinct plotted positions — Rx reads value on the left axis, Dx inventory on the right. */
  positions: number;
  /** SKUs sharing their exact position with at least one other SKU. */
  shared: number;
  /** Members of the busiest single position. */
  heaviest: number;
  /** Distinct brand × pack families — why the coincidence is structural, not noise. */
  families: number;
}

export function positionCoincidence(skus: readonly Sku[]): Coincidence {
  const at = new Map<string, number>();
  const fams = new Set<string>();
  for (const s of skus) {
    // The pair the plot actually paints: Dx is judged on the inventory it consumes, Rx on money.
    const key = `${s.type}|${s.vel}|${s.type === "Dx" ? s.inv : s.value}`;
    at.set(key, (at.get(key) ?? 0) + 1);
    fams.add(`${s.brand}|${s.size}`);
  }
  let shared = 0;
  let heaviest = 0;
  for (const n of at.values()) {
    if (n > 1) {
      shared += n;
    }
    heaviest = Math.max(heaviest, n);
  }
  return {
    total: skus.length,
    positions: at.size,
    shared,
    heaviest,
    families: fams.size,
  };
}
